import { create } from 'zustand';
import { authService, tokenStorage, User, LoginRequest, RegisterRequest } from '../services/authService';

interface AuthState {
    // State
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;

    // Actions
    login: (data: LoginRequest) => Promise<boolean>;
    register: (data: RegisterRequest) => Promise<boolean>;
    logout: () => Promise<void>;
    checkAuth: () => void;
    clearError: () => void;
    // OAuth helpers
    setToken: (token: string) => void;
    fetchProfile: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
    // Initial state
    user: tokenStorage.getUser(),
    isAuthenticated: authService.isAuthenticated(),
    isLoading: false,
    error: null,

    // Login action
    login: async (data: LoginRequest) => {
        set({ isLoading: true, error: null });
        try {
            const response = await authService.login(data);
            set({
                user: response.user,
                isAuthenticated: true,
                isLoading: false,
                error: null
            });
            return true;
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Đăng nhập thất bại';
            set({ isLoading: false, error: message });
            return false;
        }
    },

    // Register action
    register: async (data: RegisterRequest) => {
        set({ isLoading: true, error: null });
        try {
            const response = await authService.register(data);
            set({
                user: response.user,
                isAuthenticated: true,
                isLoading: false,
                error: null
            });
            return true;
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Đăng ký thất bại';
            set({ isLoading: false, error: message });
            return false;
        }
    },

    // Logout action
    logout: async () => {
        set({ isLoading: true });
        await authService.logout();
        set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null
        });
    },

    // Check if user is authenticated (restore from storage)
    checkAuth: () => {
        const user = tokenStorage.getUser();
        const isAuthenticated = authService.isAuthenticated();
        set({ user, isAuthenticated });
    },

    // Clear error
    clearError: () => {
        set({ error: null });
    },

    // Set token from OAuth callback
    setToken: (token: string) => {
        tokenStorage.setToken(token);
        set({ isAuthenticated: true });
    },

    // Fetch user profile after OAuth login
    fetchProfile: async () => {
        try {
            const user = await authService.getProfile();
            tokenStorage.setUser(user);
            set({ user, isAuthenticated: true, isLoading: false });
        } catch (error) {
            console.error('Failed to fetch profile:', error);
            // Token might be invalid, clear auth
            tokenStorage.clear();
            set({ user: null, isAuthenticated: false, isLoading: false });
        }
    }
}));

export default useAuthStore;
